import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Clock, Loader2 } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@nasam/ui";
import { authController, useAppSessionTimeout } from "../auth";

/**
 * Mounted once in AppProviders. Warns an idle user shortly before the idle
 * logout fires (see useAppSessionTimeout), with a live countdown and a way to
 * keep the session. Activity alone doesn't dismiss it -- once prompted, the
 * idle timer only restarts via "Stay signed in".
 */
export function SessionTimeoutListener() {
  const { t } = useTranslation();
  const { promptOpen, getRemainingTime, isPrompted, staySignedIn, closePrompt } =
    useAppSessionTimeout();
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    if (!promptOpen) return;
    const tick = () => {
      // Another tab may have chosen "Stay signed in" (crossTab sync).
      if (!isPrompted()) {
        closePrompt();
        return;
      }
      setSecondsLeft(Math.max(0, Math.ceil(getRemainingTime() / 1000)));
    };
    tick();
    const id = window.setInterval(tick, 500);
    return () => window.clearInterval(id);
  }, [promptOpen, getRemainingTime, isPrompted, closePrompt]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await authController.logout("manual");
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <Dialog open={promptOpen}>
      <DialogContent
        closeLabel={t("common:close")}
        showCloseButton={false}
        // Dismissing by Escape/outside click would leave the timer running
        // with no warning on screen -- the user must pick an action.
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        // Radix focuses the first button by default, drawing its focus ring
        // on open -- start with nothing focused instead.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
        className="gap-4 rounded-lg border-0 p-0 pb-4 sm:max-w-lg [--focus-ring:var(--color-badge-text-orange)]"
      >
        <div className="flex h-16 items-center gap-2 rounded-t-lg bg-badge-text-orange px-4">
          <div className="flex size-10 items-center justify-center rounded-lg bg-badge-bg-light-orange">
            <Clock className="size-6 text-badge-text-orange" />
          </div>
          <DialogTitle className="text-primary-button-text">
            {t("authentication:sessionTimeoutWarningTitle")}
          </DialogTitle>
        </div>

        <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
          <DialogDescription className="text-body-l-r text-text">
            {t("authentication:sessionTimeoutWarningMessage")}
          </DialogDescription>
          <p
            dir="ltr"
            className="text-heading-h3-bold text-badge-text-orange tabular-nums"
          >
            {formatCountdown(secondsLeft)}
          </p>
        </div>

        <div className="flex flex-col gap-3 px-4 sm:flex-row sm:gap-6 sm:rtl:flex-row-reverse">
          <Button
            type="button"
            variant="secondary"
            size="lg"
            data-testid="session-timeout-logout-button"
            disabled={isLoggingOut}
            onClick={() => void handleLogout()}
            className="w-full border-0 bg-transparent shadow-none hover:bg-container-hover sm:flex-1"
          >
            {isLoggingOut && <Loader2 className="size-4 animate-spin" />}
            {t("common:logout")}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="lg"
            data-testid="session-timeout-stay-button"
            disabled={isLoggingOut}
            onClick={staySignedIn}
            className="w-full bg-badge-text-orange hover:bg-badge-text-orange-hover disabled:bg-badge-text-orange disabled:opacity-60 sm:flex-1"
          >
            {t("authentication:sessionTimeoutStayButton")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}
